import { TextCellEditor } from "../../rendering/cellEditors/textCellEditor";
import { Autowired, Bean, PostConstruct } from "../../context/context";
import { IComponent } from "../../interfaces/iComponent";
import { DateFilter } from "../../filter/provided/date/dateFilter";
import { HeaderComp } from "../../headerRendering/header/headerComp";
import { HeaderGroupComp } from "../../headerRendering/headerGroup/headerGroupComp";
import { GroupCellRenderer } from "../../rendering/cellRenderers/groupCellRenderer";
import { AnimateShowChangeCellRenderer } from "../../rendering/cellRenderers/animateShowChangeCellRenderer";
import { AnimateSlideCellRenderer } from "../../rendering/cellRenderers/animateSlideCellRenderer";
import { LoadingCellRenderer } from "../../rendering/cellRenderers/loadingCellRenderer";
import { SelectCellEditor } from "../../rendering/cellEditors/selectCellEditor";
import { PopupTextCellEditor } from "../../rendering/cellEditors/popupTextCellEditor";
import { PopupSelectCellEditor } from "../../rendering/cellEditors/popupSelectCellEditor";
import { LargeTextCellEditor } from "../../rendering/cellEditors/largeTextCellEditor";
import { NumberFilter } from "../../filter/provided/number/numberFilter";
import { LoadingOverlayComponent } from "../../rendering/overlays/loadingOverlayComponent";
import { NoRowsOverlayComponent } from "../../rendering/overlays/noRowsOverlayComponent";
import { TooltipComponent } from "../../rendering/tooltipComponent";
import { GridOptions } from "../../entities/gridOptions";
import { DefaultDateComponent } from "../../filter/provided/date/defaultDateComponent";
import { DateFloatingFilter } from "../../filter/provided/date/dateFloatingFilter";
import { TextFilter } from "../../filter/provided/text/textFilter";
import { NumberFloatingFilter } from "../../filter/provided/number/numberFloatingFilter";
import { TextFloatingFilter } from "../../filter/provided/text/textFloatingFilter";
import { BeanStub } from "../../context/beanStub";
import { iterateObject } from '../../utils/object';
import { doOnce } from "../../utils/function";

export enum RegisteredComponentSource {
  DEFAULT, REGISTERED
}

/**
 * B the business interface (ie IHeader)
 * A the dyGridComponent interface (ie IHeaderComp). The final object acceptable by dyna-grid
 */
export interface RegisteredComponent<A extends IComponent<any> & B, B> {
  component: RegisteredComponentInput<A, B>;
  componentFromFramework: boolean;
  source: RegisteredComponentSource;
}

export type RegisteredComponentInput<A extends IComponent<any> & B, B> =
  DyGridRegisteredComponentInput<A>
  | { new(): B; };
export type DyGridRegisteredComponentInput<A extends IComponent<any>> = DyGridComponentFunctionInput | { new(): A; };
export type DyGridComponentFunctionInput = (params: any) => string | HTMLElement;

export interface DeprecatedComponentName {
  propertyHolder: string;
  newComponentName: string;
}

@Bean('userComponentRegistry')
export class UserComponentRegistry extends BeanStub {

  @Autowired('gridOptions')
  private gridOptions: GridOptions;

  private dyGridDefaults: { [key: string]: DyGridRegisteredComponentInput<any>; } = {
    //date
    dyDateInput: DefaultDateComponent,

    //header
    dyColumnHeader: HeaderComp,
    dyColumnGroupHeader: HeaderGroupComp,

    //floating filters
    dyTextColumnFloatingFilter: TextFloatingFilter,
    dyNumberColumnFloatingFilter: NumberFloatingFilter,
    dyDateColumnFloatingFilter: DateFloatingFilter,

    // renderers
    dyAnimateShowChangeCellRenderer: AnimateShowChangeCellRenderer,
    dyAnimateSlideCellRenderer: AnimateSlideCellRenderer,
    dyGroupCellRenderer: GroupCellRenderer,
    dyGroupRowRenderer: GroupCellRenderer,
    dyLoadingCellRenderer: LoadingCellRenderer,

    //editors
    dyCellEditor: TextCellEditor,
    dyTextCellEditor: TextCellEditor,
    dySelectCellEditor: SelectCellEditor,
    dyPopupTextCellEditor: PopupTextCellEditor,
    dyPopupSelectCellEditor: PopupSelectCellEditor,
    dyLargeTextCellEditor: LargeTextCellEditor,

    //filter
    dyTextColumnFilter: TextFilter,
    dyNumberColumnFilter: NumberFilter,
    dyDateColumnFilter: DateFilter,

    //overlays
    dyLoadingOverlay: LoadingOverlayComponent,
    dyNoRowsOverlay: NoRowsOverlayComponent,

    // tooltips
    dyTooltipComponent: TooltipComponent
  };

  private dyDeprecatedNames: { [key: string]: DeprecatedComponentName; } = {
    set: {
      newComponentName: 'dySetColumnFilter',
      propertyHolder: 'filter'
    },
    text: {
      newComponentName: 'dyTextColumnFilter',
      propertyHolder: 'filter'
    },
    number: {
      newComponentName: 'dyNumberColumnFilter',
      propertyHolder: 'filter'
    },
    date: {
      newComponentName: 'dyDateColumnFilter',
      propertyHolder: 'filter'
    },

    group: {
      newComponentName: 'dyGroupCellRenderer',
      propertyHolder: 'cellRenderer'
    },
    animateShowChange: {
      newComponentName: 'dyAnimateShowChangeCellRenderer',
      propertyHolder: 'cellRenderer'
    },
    animateSlide: {
      newComponentName: 'dyAnimateSlideCellRenderer',
      propertyHolder: 'cellRenderer'
    },

    select: {
      newComponentName: 'dySelectCellEditor',
      propertyHolder: 'cellEditor'
    },
    largeText: {
      newComponentName: 'dyLargeTextCellEditor',
      propertyHolder: 'cellEditor'
    },
    popupSelect: {
      newComponentName: 'dyPopupSelectCellEditor',
      propertyHolder: 'cellEditor'
    },
    popupText: {
      newComponentName: 'dyPopupTextCellEditor',
      propertyHolder: 'cellEditor'
    },
    richSelect: {
      newComponentName: 'dyRichSelectCellEditor',
      propertyHolder: 'cellEditor'
    },

    headerComponent: {
      newComponentName: 'dyColumnHeader',
      propertyHolder: 'headerComponent'
    }

  };
  private jsComponents: { [key: string]: DyGridRegisteredComponentInput<any>; } = {};
  private frameworkComponents: { [key: string]: { new(): any; }; } = {};

  @PostConstruct
  private init(): void {
    if (this.gridOptions.components != null) {
      iterateObject(this.gridOptions.components, (key, component) => this.registerComponent(key, component));
    }

    if (this.gridOptions.frameworkComponents != null) {
      iterateObject(this.gridOptions.frameworkComponents,
        (key, component) => this.registerFwComponent(key, component as any));
    }
  }

  public registerDefaultComponent<A extends IComponent<any>>(rawName: string, component: DyGridRegisteredComponentInput<A>) {
    const name = this.translateIfDeprecated(rawName);

    if (this.dyGridDefaults[name]) {
      console.error(`Trying to overwrite a default component. You should call registerComponent`);
      return;
    }

    this.dyGridDefaults[name] = component;
  }

  public registerComponent<A extends IComponent<any>>(rawName: string, component: DyGridRegisteredComponentInput<A>) {
    const name = this.translateIfDeprecated(rawName);

    if (this.frameworkComponents[name]) {
      console.error(`Trying to register a component that you have already registered for frameworks: ${name}`);
      return;
    }

    this.jsComponents[name] = component;
  }

  /**
   * B the business interface (ie IHeader)
   * A the dyGridComponent interface (ie IHeaderComp). The final object acceptable by dyna-grid
   */
  public registerFwComponent<A extends IComponent<any> & B, B>(rawName: string, component: { new(): IComponent<B>; }) {
    const name = this.translateIfDeprecated(rawName);

    if (this.jsComponents[name]) {
      console.error(`Trying to register a component that you have already registered for plain javascript: ${name}`);
      return;
    }

    this.frameworkComponents[name] = component;
  }

  /**
   * B the business interface (ie IHeader)
   * A the dyGridComponent interface (ie IHeaderComp). The final object acceptable by dyna-grid
   */
  public retrieve<A extends IComponent<any> & B, B>(rawName: string): RegisteredComponent<A, B> | null {
    const name = this.translateIfDeprecated(rawName);
    const frameworkComponent = this.frameworkComponents[name];

    if (frameworkComponent) {
      return {
        componentFromFramework: true,
        component: frameworkComponent as { new(): B; },
        source: RegisteredComponentSource.REGISTERED
      };
    }

    const jsComponent = this.jsComponents[name];

    if (jsComponent) {
      return {
        componentFromFramework: false,
        component: jsComponent as { new(): A; },
        source: RegisteredComponentSource.REGISTERED
      };
    }

    const defaultComponent = this.dyGridDefaults[name];

    if (defaultComponent) {
      return {
        componentFromFramework: false,
        component: defaultComponent as { new(): A; },
        source: RegisteredComponentSource.DEFAULT
      };
    }

    if (Object.keys(this.dyGridDefaults).indexOf(name) < 0) {
      console.warn(`DYNA Grid: Looking for component [${name}] but it wasn't found.`);
    }

    return null;
  }

  private translateIfDeprecated(raw: string): string {
    const deprecatedInfo = this.dyDeprecatedNames[raw];

    if (deprecatedInfo != null) {
      doOnce(() => {
        console.warn(`dyna-grid. component names have been renamed to be namespaced. You should rename ${deprecatedInfo.propertyHolder}:${raw} to ${deprecatedInfo.propertyHolder}:${deprecatedInfo.newComponentName}`);
      }, 'DEPRECATE_COMPONENT_' + raw);

      return deprecatedInfo.newComponentName;
    }

    return raw;
  }
}
